import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/config';
import DashboardHeader from '../common/DashboardHeader';
import {
  CalendarIcon,
  DocumentTextIcon,
  CreditCardIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
  BellIcon,
  StarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  PlusIcon,
  PhoneIcon,
  MapPinIcon,
  BanknotesIcon,
  TagIcon,
  ReceiptRefundIcon,
  ChartBarIcon,
  UserGroupIcon,
  TruckIcon,
  SparklesIcon,
  HeartIcon,
  ShieldCheckIcon,
  PhotoIcon,
  ChatBubbleLeftRightIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface Vehicle {
  id: string;
  make: string;
  model: string;
  year: number;
  plateNumber: string;
  vin: string;
  color: string;
  lastService: string;
  nextServiceDue: string;
  mileage: number;
  image?: string;
}

interface ServiceHistory {
  id: string;
  jobCardId: string;
  date: string;
  garage: string;
  services: string[];
  cost: number;
  status: 'completed' | 'in_progress' | 'scheduled';
  technician: string;
  rating?: number;
  feedback?: string;
  invoice?: string;
  images?: string[];
}

interface Appointment {
  id: string;
  vehicleId: string;
  vehicle: string;
  garage: string;
  service: string;
  date: string;
  time: string;
  status: 'confirmed' | 'pending' | 'cancelled';
  estimatedCost: number;
  notes?: string;
}

interface CustomerStats {
  totalVehicles: number;
  totalServices: number;
  totalSpent: number;
  upcomingAppointments: number;
  loyaltyPoints: number;
  memberSince: string;
  preferredGarage?: string;
  lastVisit?: string;
}

interface Promotion {
  id: string;
  title: string;
  description: string;
  discount: number;
  validUntil: string;
  code: string;
  applicableServices: string[];
}

interface ServiceReminder {
  id: string;
  vehicleId: string;
  vehicle: string;
  service: string;
  dueDate: string;
  mileage?: number;
  priority: 'urgent' | 'soon' | 'upcoming';
}

const EnhancedCustomerDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<CustomerStats | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [serviceHistory, setServiceHistory] = useState<ServiceHistory[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [reminders, setReminders] = useState<ServiceReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState('overview');
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // In production, these would be actual API calls
      // For now, setting mock data
      setMockData();
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const setMockData = () => {
    setStats({
      totalVehicles: 2,
      totalServices: 24,
      totalSpent: 3456.750,
      upcomingAppointments: 1,
      loyaltyPoints: 850,
      memberSince: '2022-03-15',
      preferredGarage: 'كراج الأمل',
      lastVisit: '2024-01-10'
    });

    setVehicles([
      {
        id: '1',
        make: 'Toyota',
        model: 'Camry',
        year: 2020,
        plateNumber: 'ABC 123',
        vin: '1HGBH41JXMN109186',
        color: 'Silver',
        lastService: '2024-01-10',
        nextServiceDue: '2024-04-10',
        mileage: 45000,
        image: '/api/placeholder/200/150'
      },
      {
        id: '2',
        make: 'Honda',
        model: 'Accord',
        year: 2019,
        plateNumber: 'XYZ 789',
        vin: '2HGBH41JXMN109187',
        color: 'Black',
        lastService: '2023-11-25',
        nextServiceDue: '2024-02-25',
        mileage: 62000,
        image: '/api/placeholder/200/150'
      }
    ]);

    setServiceHistory([
      {
        id: '1',
        jobCardId: 'JC-2024-001',
        date: '2024-01-10',
        garage: 'كراج الأمل',
        services: ['Oil Change', 'Filter Replacement', 'Tire Rotation'],
        cost: 45.750,
        status: 'completed',
        technician: 'Ahmed Hassan',
        rating: 5,
        feedback: 'Excellent service, very professional',
        invoice: 'INV-2024-001'
      },
      {
        id: '2',
        jobCardId: 'JC-2023-456',
        date: '2023-11-25',
        garage: 'مركز الصيانة المتقدم',
        services: ['Brake Pad Replacement', 'Brake Fluid Change'],
        cost: 120.500,
        status: 'completed',
        technician: 'Mohammed Ali',
        rating: 4,
        feedback: 'Good service but took longer than expected'
      },
      {
        id: '3',
        jobCardId: 'JC-2023-789',
        date: '2023-09-15',
        garage: 'كراج الأمل',
        services: ['AC Service', 'Refrigerant Recharge'],
        cost: 85.250,
        status: 'completed',
        technician: 'Khalid Omar',
        rating: 5
      },
      {
        id: '4',
        jobCardId: 'JC-2024-002',
        date: '2024-01-20',
        garage: 'كراج الأمل',
        services: ['Engine Diagnostics'],
        cost: 0,
        status: 'scheduled',
        technician: 'TBD'
      }
    ]);

    setAppointments([
      {
        id: '1',
        vehicleId: '1',
        vehicle: 'Toyota Camry 2020',
        garage: 'كراج الأمل',
        service: 'Regular Maintenance',
        date: '2024-01-20',
        time: '10:00 AM',
        status: 'confirmed',
        estimatedCost: 65.000,
        notes: 'Oil change due, check brakes'
      }
    ]);

    setPromotions([
      {
        id: '1',
        title: 'Winter Service Special',
        description: '20% off on all AC services',
        discount: 20,
        validUntil: '2024-02-29',
        code: 'WINTER20',
        applicableServices: ['AC Service', 'AC Repair']
      },
      {
        id: '2',
        title: 'Loyalty Reward',
        description: 'Free oil change after 5 paid services',
        discount: 100,
        validUntil: '2024-12-31',
        code: 'LOYAL5',
        applicableServices: ['Oil Change']
      },
      {
        id: '3',
        title: 'New Year Offer',
        description: '15% off on brake services',
        discount: 15,
        validUntil: '2024-01-31',
        code: 'NY2024',
        applicableServices: ['Brake Service', 'Brake Pad Replacement']
      }
    ]);

    setReminders([
      {
        id: '1',
        vehicleId: '2',
        vehicle: 'Honda Accord 2019',
        service: 'Oil Change',
        dueDate: '2024-02-25',
        mileage: 65000,
        priority: 'soon'
      },
      {
        id: '2',
        vehicleId: '1',
        vehicle: 'Toyota Camry 2020',
        service: 'Tire Rotation',
        dueDate: '2024-03-10',
        priority: 'upcoming'
      },
      {
        id: '3',
        vehicleId: '2',
        vehicle: 'Honda Accord 2019',
        service: 'Brake Inspection',
        dueDate: '2024-02-01',
        priority: 'urgent'
      }
    ]);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KW', {
      style: 'currency',
      currency: 'KWD',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(amount);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <StarSolid
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  const getReminderColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-300';
      case 'soon': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'upcoming': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const renderVehicleCard = (vehicle: Vehicle) => {
    const isSelected = selectedVehicle === vehicle.id;
    const daysUntilService = Math.ceil((new Date(vehicle.nextServiceDue).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    const needsService = daysUntilService <= 30;

    return (
      <div
        key={vehicle.id}
        onClick={() => setSelectedVehicle(vehicle.id)}
        className={`border rounded-xl p-6 cursor-pointer transition-all ${
          isSelected ? 'border-[#008B8B] shadow-lg bg-[#008B8B]/5' : 'border-gray-200 hover:shadow-md'
        }`}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {vehicle.make} {vehicle.model} {vehicle.year}
            </h3>
            <p className="text-sm text-gray-600">Plate: {vehicle.plateNumber}</p>
          </div>
          {needsService && (
            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
              Service Due
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-gray-600">Mileage</p>
            <p className="font-medium">{vehicle.mileage.toLocaleString()} km</p>
          </div>
          <div>
            <p className="text-gray-600">Color</p>
            <p className="font-medium">{vehicle.color}</p>
          </div>
          <div>
            <p className="text-gray-600">Last Service</p>
            <p className="font-medium">{new Date(vehicle.lastService).toLocaleDateString()}</p>
          </div>
          <div>
            <p className="text-gray-600">Next Service</p>
            <p className={`font-medium ${needsService ? 'text-yellow-600' : ''}`}>
              {new Date(vehicle.nextServiceDue).toLocaleDateString()}
            </p>
          </div>
        </div>

        <div className="mt-4 flex space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowBookingModal(true);
            }}
            className="flex-1 px-3 py-2 bg-[#008B8B] text-white text-sm rounded-lg hover:bg-[#006666]"
          >
            Book Service
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/dashboard/vehicles/${vehicle.id}`);
            }}
            className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
          >
            View Details
          </button>
        </div>
      </div>
    );
  };

  const renderServiceHistoryTable = () => {
    const filteredHistory = serviceHistory.filter(service => {
      const matchesSearch = service.garage.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          service.services.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesFilter = serviceFilter === 'all' || service.status === serviceFilter;
      return matchesSearch && matchesFilter;
    });

    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Service History</h3>
          <button className="text-sm text-[#008B8B] hover:text-[#006666] font-medium flex items-center">
            <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
            Export History
          </button>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search services..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
            />
          </div>
          <select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B]"
          >
            <option value="all">All Services</option>
            <option value="completed">Completed</option>
            <option value="scheduled">Scheduled</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Date</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Services</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Garage</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Cost</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Rating</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredHistory.map((service) => (
                <tr key={service.id} className="hover:bg-gray-50">
                  <td className="py-4 px-4">
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(service.date).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-gray-500">{service.jobCardId}</p>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex flex-wrap gap-1">
                      {service.services.map((s, i) => (
                        <span key={i} className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                          {s}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <p className="text-sm text-gray-700">{service.garage}</p>
                    <p className="text-xs text-gray-500">{service.technician}</p>
                  </td>
                  <td className="py-4 px-4 text-sm font-medium">
                    {service.cost > 0 ? formatCurrency(service.cost) : '-'}
                  </td>
                  <td className="py-4 px-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      service.status === 'completed' ? 'bg-green-100 text-green-800' :
                      service.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {service.status}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    {service.rating ? (
                      <div className="flex items-center">
                        {renderStars(service.rating)}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex space-x-2">
                      {service.invoice && (
                        <button className="text-[#008B8B] hover:text-[#006666]">
                          <DocumentTextIcon className="w-5 h-5" />
                        </button>
                      )}
                      {service.status === 'completed' && !service.rating && (
                        <button className="text-yellow-600 hover:text-yellow-700">
                          <StarIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderAppointmentCard = (appointment: Appointment) => {
    const statusColors = {
      confirmed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800'
    };

    return (
      <div key={appointment.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="font-semibold text-gray-900">{appointment.service}</h4>
            <p className="text-sm text-gray-600">{appointment.vehicle}</p>
          </div>
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[appointment.status]}`}>
            {appointment.status}
          </span>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center text-gray-600">
            <CalendarIcon className="w-4 h-4 mr-2" />
            {new Date(appointment.date).toLocaleDateString()} at {appointment.time}
          </div>
          <div className="flex items-center text-gray-600">
            <MapPinIcon className="w-4 h-4 mr-2" />
            {appointment.garage}
          </div>
          <div className="flex items-center text-gray-600">
            <BanknotesIcon className="w-4 h-4 mr-2" />
            Estimated: {formatCurrency(appointment.estimatedCost)}
          </div>
        </div>

        {appointment.notes && (
          <p className="mt-3 text-sm text-gray-700 bg-gray-50 p-2 rounded">
            {appointment.notes}
          </p>
        )}

        <div className="mt-4 flex space-x-2">
          {appointment.status === 'confirmed' && (
            <>
              <button className="flex-1 px-3 py-2 bg-[#008B8B] text-white text-sm rounded hover:bg-[#006666]">
                Add to Calendar
              </button>
              <button className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50">
                Reschedule
              </button>
            </>
          )}
          {appointment.status === 'pending' && (
            <button className="flex-1 px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700">
              Cancel
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderPromotions = () => {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Special Offers</h3>
          <SparklesIcon className="w-6 h-6 text-yellow-500" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {promotions.map((promo) => (
            <div
              key={promo.id}
              className="border border-dashed border-[#008B8B] rounded-lg p-4 bg-gradient-to-br from-[#008B8B]/5 to-[#008B8B]/10"
            >
              <div className="flex items-start justify-between mb-3">
                <TagIcon className="w-6 h-6 text-[#008B8B]" />
                <span className="text-2xl font-bold text-[#008B8B]">{promo.discount}%</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-1">{promo.title}</h4>
              <p className="text-sm text-gray-600 mb-3">{promo.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Valid until {new Date(promo.validUntil).toLocaleDateString()}</span>
                <span className="px-2 py-1 bg-[#008B8B] text-white text-xs rounded font-mono">
                  {promo.code}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderServiceReminders = () => {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Service Reminders</h3>
          <BellIcon className="w-6 h-6 text-red-500" />
        </div>

        <div className="space-y-3">
          {reminders.map((reminder) => (
            <div
              key={reminder.id}
              className={`border rounded-lg p-4 ${getReminderColor(reminder.priority)}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium">{reminder.service}</h4>
                  <p className="text-sm mt-1">{reminder.vehicle}</p>
                  <div className="flex items-center mt-2 text-sm">
                    <CalendarIcon className="w-4 h-4 mr-1" />
                    Due: {new Date(reminder.dueDate).toLocaleDateString()}
                    {reminder.mileage && (
                      <span className="ml-3">or at {reminder.mileage.toLocaleString()} km</span>
                    )}
                  </div>
                </div>
                <button className="px-3 py-1 bg-white text-gray-700 text-sm rounded hover:bg-gray-50">
                  Book Now
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLoyaltySection = () => {
    const nextReward = 1000 - (stats?.loyaltyPoints || 0);
    const progress = ((stats?.loyaltyPoints || 0) / 1000) * 100;

    return (
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Loyalty Program</h3>
            <p className="text-purple-200">Member since {stats?.memberSince ? new Date(stats.memberSince).getFullYear() : ''}</p>
          </div>
          <HeartIcon className="w-8 h-8 text-purple-300" />
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-3xl font-bold">{stats?.loyaltyPoints || 0}</span>
            <span className="text-sm text-purple-200">points</span>
          </div>
          <div className="w-full bg-purple-800 rounded-full h-3">
            <div 
              className="bg-white h-3 rounded-full transition-all duration-300"
              style={{width: `${progress}%`}}
            ></div>
          </div>
          <p className="text-sm text-purple-200 mt-2">
            {nextReward} points to next reward
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <button className="px-4 py-2 bg-white text-purple-700 rounded-lg hover:bg-purple-50 font-medium">
            View Rewards
          </button>
          <button className="px-4 py-2 bg-purple-800 text-white rounded-lg hover:bg-purple-900">
            Earn Points
          </button>
        </div>
      </div>
    );
  };

  const renderSpendingAnalysis = () => {
    const monthlySpending = [280, 320, 185, 420, 290, 340];
    const serviceBreakdown = [
      { name: 'Oil Change', value: 35 },
      { name: 'Brake Service', value: 25 },
      { name: 'AC Service', value: 20 },
      { name: 'Engine Repair', value: 15 },
      { name: 'Other', value: 5 }
    ];

    const lineData = {
      labels: ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'],
      datasets: [{
        label: 'Monthly Spending',
        data: monthlySpending,
        borderColor: 'rgb(0, 139, 139)',
        backgroundColor: 'rgba(0, 139, 139, 0.1)',
        fill: true,
        tension: 0.4
      }]
    };

    const doughnutData = {
      labels: serviceBreakdown.map(s => s.name),
      datasets: [{
        data: serviceBreakdown.map(s => s.value),
        backgroundColor: [
          'rgba(0, 139, 139, 0.8)',
          'rgba(255, 140, 0, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(139, 92, 246, 0.8)'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom' as const,
        }
      }
    };

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Spending Trend</h3>
          <div className="h-64">
            <Line data={lineData} options={options} />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Breakdown</h3>
          <div className="h-64">
            <Doughnut data={doughnutData} options={options} />
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader 
        userName={user?.name}
        userRole="customer"
        showNotifications={true}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Welcome back, {user?.name || 'Customer'}
              </h1>
              <p className="text-gray-600 mt-1">
                Manage your vehicles and service appointments
              </p>
            </div>
            <div className="mt-4 lg:mt-0 flex flex-wrap gap-3">
              <div className="flex bg-white rounded-lg shadow">
                {['overview', 'vehicles', 'history', 'appointments'].map((view) => (
                  <button
                    key={view}
                    onClick={() => setSelectedView(view)}
                    className={`px-4 py-2 text-sm font-medium capitalize ${
                      selectedView === view
                        ? 'bg-[#008B8B] text-white'
                        : 'text-gray-700 hover:bg-gray-50'
                    } ${
                      view === 'overview' ? 'rounded-l-lg' : 
                      view === 'appointments' ? 'rounded-r-lg' : ''
                    }`}
                  >
                    {view}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setShowBookingModal(true)}
                className="px-4 py-2 bg-[#008B8B] text-white rounded-lg hover:bg-[#006666] flex items-center"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Book Service
              </button>
            </div>
          </div>
        </div>

        {/* Key Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">My Vehicles</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">{stats?.totalVehicles || 0}</p>
              </div>
              <TruckIcon className="w-8 h-8 text-[#008B8B]" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Services</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">{stats?.totalServices || 0}</p>
              </div>
              <WrenchScrewdriverIcon className="w-8 h-8 text-[#FF8C00]" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Spent</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">
                  {formatCurrency(stats?.totalSpent || 0)}
                </p>
              </div>
              <CreditCardIcon className="w-8 h-8 text-[#10B981]" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Appointments</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">{stats?.upcomingAppointments || 0}</p>
              </div>
              <CalendarIcon className="w-8 h-8 text-[#F59E0B]" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Loyalty Points</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">{stats?.loyaltyPoints || 0}</p>
              </div>
              <StarIcon className="w-8 h-8 text-[#8B5CF6]" />
            </div>
          </div>
        </div>

        {/* Content based on selected view */}
        {selectedView === 'overview' && (
          <>
            {/* Service Reminders and Promotions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {renderServiceReminders()}
              {renderLoyaltySection()}
            </div>

            {/* My Vehicles */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">My Vehicles</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {vehicles.map(renderVehicleCard)}
              </div>
            </div>

            {/* Promotions */}
            {renderPromotions()}

            {/* Spending Analysis */}
            <div className="mt-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Spending Analysis</h2>
              {renderSpendingAnalysis()}
            </div>
          </>
        )}

        {selectedView === 'vehicles' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">My Vehicles</h2>
              <button className="px-4 py-2 bg-[#008B8B] text-white rounded-lg hover:bg-[#006666] flex items-center">
                <PlusIcon className="w-5 h-5 mr-2" />
                Add Vehicle
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {vehicles.map(renderVehicleCard)}
            </div>
          </div>
        )}

        {selectedView === 'history' && renderServiceHistoryTable()}

        {selectedView === 'appointments' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">My Appointments</h2>
              <button
                onClick={() => setShowBookingModal(true)}
                className="px-4 py-2 bg-[#008B8B] text-white rounded-lg hover:bg-[#006666] flex items-center"
              >
                <CalendarIcon className="w-5 h-5 mr-2" />
                Schedule New
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {appointments.map(renderAppointmentCard)}
            </div>

            {appointments.length === 0 && (
              <div className="bg-gray-100 rounded-xl p-8 text-center">
                <CalendarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No upcoming appointments</p>
                <button
                  onClick={() => setShowBookingModal(true)}
                  className="mt-4 px-6 py-2 bg-[#008B8B] text-white rounded-lg hover:bg-[#006666]"
                >
                  Book Your First Service
                </button>
              </div>
            )}
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button
              onClick={() => setShowBookingModal(true)}
              className="p-4 bg-white rounded-xl shadow hover:shadow-lg transition-all text-center group"
            >
              <CalendarIcon className="w-8 h-8 text-[#008B8B] mx-auto mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium">Book Service</span>
            </button>
            <button
              onClick={() => navigate('/dashboard/vehicles')}
              className="p-4 bg-white rounded-xl shadow hover:shadow-lg transition-all text-center group"
            >
              <TruckIcon className="w-8 h-8 text-[#FF8C00] mx-auto mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium">My Vehicles</span>
            </button>
            <button
              onClick={() => navigate('/dashboard/promotions')}
              className="p-4 bg-white rounded-xl shadow hover:shadow-lg transition-all text-center group"
            >
              <TagIcon className="w-8 h-8 text-[#10B981] mx-auto mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium">View Offers</span>
            </button>
            <button
              onClick={() => navigate('/dashboard/support')}
              className="p-4 bg-white rounded-xl shadow hover:shadow-lg transition-all text-center group"
            >
              <ChatBubbleLeftRightIcon className="w-8 h-8 text-[#8B5CF6] mx-auto mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium">Get Support</span>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EnhancedCustomerDashboard;